import React, { useEffect, useState } from 'react';
import DashboardSidebar from '@/components/dashboard/DashboardSidebar';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import MarketInsights from '@/components/dashboard/MarketInsights';
import CompetitiveAnalysis from '@/components/dashboard/CompetitiveAnalysis';
import RateParity from '@/components/dashboard/RateParity';
import ReportsGenerator from '@/components/dashboard/ReportsGenerator';
import AdvancedAnalytics from '@/components/dashboard/AdvancedAnalytics';
import AIAssistant from '@/components/dashboard/AIAssistant';
import CompsetSettings from '@/components/dashboard/CompsetSettings';
import TrialExpiryBanner from '@/components/dashboard/TrialExpiryBanner';
import RunLogs from '@/components/dashboard/RunLogs';
import ScheduledRuns from '@/components/dashboard/ScheduledRuns';
import Invoices from '@/components/dashboard/Invoices';
import ProfilePage from '@/components/dashboard/ProfilePage';
import PlanSelection from '@/components/dashboard/PlanSelection';
import { DashboardFiltersProvider } from '@/contexts/DashboardFilters';
import { Loader2 } from 'lucide-react';
import { useCompset } from '@/contexts/CompsetContext';
import ScrollToTop from '@/components/ScrollToTop';
import { useDashboard } from '@/contexts/DashboardContext';
import DowngradeCompsetModal from '@/components/modals/DowngradeCompsetModal';
import { usePlans } from '@/contexts/PlanRulesContext';
import DashboardError from '@/components/error/DashboardError';
import WhatsAppWidget from '@/components/WhatsappWidget';
import HowItWorks from '@/components/dashboard/HowItWorks';
import { useAuth } from '@/contexts/AuthContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';

function DashboardContent() {
  const { activeView, setActiveView } = useDashboard();

  // ── Breakpoints ─────────────────────────────────────────────────────────────
  const isMobile = useMediaQuery('(max-width: 767px)');   // phones
  const isTablet = useMediaQuery('(max-width: 1023px)');  // tablets (but not phones)

  // Initialise collapsed correctly on first render using a lazy initialiser
  // so there is never a flash before the useEffect fires.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 1023px)').matches; // true on mobile & tablet
  });

  // Keep collapsed state in sync when the user resizes the window
  useEffect(() => {
    if (isMobile || isTablet) {
      setSidebarCollapsed(true);
    } else {
      setSidebarCollapsed(false);
    }
  }, [isMobile, isTablet]);

  const [showAI, setShowAI] = useState(false);
  const { isLoadingCompsets, isErrorCompsets, compsets } = useCompset();
  const { isPlanActive, isLoadingPlanRules, maxCompetitorsPerCompset } = usePlans();
  const [showReduceCompModal, setShowReduceCompModal] = useState(false);

  useEffect(() => {
    if (!compsets || isLoadingCompsets || isErrorCompsets) return;
    const exceeded = compsets?.user_compsets.find(
      (comp) => comp?.competitors?.length > maxCompetitorsPerCompset
    );
    if (exceeded) setShowReduceCompModal(true);
  }, [compsets, isLoadingCompsets, isErrorCompsets]);

  const renderContent = () => {
    if (isLoadingCompsets) {
      return (
        <div className="flex items-center justify-center min-h-[85vh]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading dashboard data...</p>
          </div>
        </div>
      );
    }
    if (isErrorCompsets) return <DashboardError />;

    switch (activeView) {
      case 'market-insights': return <MarketInsights />;
      case 'competitive-analysis': return <CompetitiveAnalysis />;
      case 'rate-parity': return <RateParity />;
      case 'reports': return <ReportsGenerator />;
      case 'advanced-analytics': return <AdvancedAnalytics />;
      case 'compset': return <CompsetSettings />;
      case 'run-logs': return <RunLogs />;
      case 'scheduled-run': return <ScheduledRuns />;
      case 'invoices': return <Invoices />;
      case 'profile': return <ProfilePage />;
      case 'plan-selection': return <PlanSelection onSelectPlan={() => setActiveView('market-insights')} />;
      case 'help': return <HowItWorks />;
      default: return <MarketInsights />;
    }
  };

  // ── Layout helpers ──────────────────────────────────────────────────────────
  // On mobile the sidebar is an overlay (not in document flow) → no margin needed.
  // On tablet/desktop the sidebar IS in flow → offset the content area.
  const contentMarginLeft = isMobile
    ? 'ml-0'
    : sidebarCollapsed
      ? 'ml-16'
      : 'ml-64';

  // Hide the AI panel on mobile to avoid squashing content
  const aiPanelOffset = showAI && !isMobile ? 'mr-80' : '';

  return (
    <div className="min-h-screen bg-background flex">
      <ScrollToTop tab={activeView} />

      <DashboardSidebar
        activeView={activeView}
        setActiveView={setActiveView}
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />

      {showReduceCompModal && !isLoadingCompsets && (
        <DowngradeCompsetModal
          open={showReduceCompModal}
          onSubmit={() => setShowReduceCompModal(false)}
        />
      )}

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${contentMarginLeft}`}>
        {!isPlanActive && !isLoadingPlanRules && (
          <TrialExpiryBanner isExpired={!isPlanActive} />
        )}

        {/*
          Pass onToggleSidebar so DashboardHeader can render a hamburger
          button (☰) on mobile that opens the drawer.
          No logic changes — DashboardHeader just calls this callback.
        */}
        <DashboardHeader
          onToggleAI={() => setShowAI((prev) => !prev)}
          showAI={showAI}
          onToggleSidebar={() => setSidebarCollapsed((prev) => !prev)}
        />

        <main className="flex-1 p-4 sm:p-6 overflow-auto bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 dark:from-slate-950 dark:via-blue-950 dark:to-slate-950">
          <div className={`transition-all duration-300 ${aiPanelOffset}`}>
            {renderContent()}
          </div>
          <div className="flex justify-start mt-4">
            <p className="text-sm text-muted-foreground">
              &copy; {new Date().getFullYear()} Prowen Technologies India P Ltd. All rights reserved.
            </p>
          </div>
        </main>
      </div>

      {showAI && (
        <AIAssistant
          isOpen
          onClose={() => setShowAI(false)}
          // On mobile render as full-screen sheet; parent component handles styling
          fullScreen={isMobile}
        />
      )}

      <WhatsAppWidget />
    </div>
  );
}

export default function DashboardPage() {
  return (
    <DashboardFiltersProvider>
      <DashboardContent />
    </DashboardFiltersProvider>
  );
}